<template>
  <div>
    <div class="config-tab tab-content">
      <div class="content" v-if="cards">
        <div class="instructions">
          <h6>Bulk Edit</h6>
          <p class="body-1">
            Edit each field you would like to apply to the selected
            {{ bulkUpdateCount }} client(s).
          </p>
        </div>
        <template v-for="card in visibleCards">
          <h6 :key="`${card.id}-header`" class="card-title">{{ card.name }}</h6>
          <card
            :key="card.id"
            :id="card.id"
            :rows="card.fields"
            :category="computedCategory(card)"
          >
            <fields :rows="card.fields" :category="computedCategory(card)" />
          </card>
        </template>
      </div>
    </div>
    <bottomBar>
      <qvoButton text @click="exitBulkSettings">Cancel</qvoButton>
      <qvoButton :disabled="!hasChanges || !isFormValid" @click="goToNextStep"
        >Next</qvoButton
      >
    </bottomBar>
  </div>
</template>

<script>
/**
 * @fileoverview Config tab
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";

import bottomBar from "@/components/bottom-bar";
import card from "@/components/bulkUpdate/card";
import qvoButton from "@/qux-common/qux22/components/molecules/button";

import fields from "./fields";

import visibleCards from "@/util/visibleCards";

export default {
  name: "BulkEditConfigTab",
  components: { bottomBar, card, qvoButton, fields },
  props: {
    tab: {
      type: String,
    },
    isFormValid: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isLoadingTemplate() {
      return this.pendingCalls.includes("/client/template/retrieve");
    },
    configData() {
      return () => import(`@/components/client/configs/${this.tab}`);
    },
    cardsToLinks() {
      if (!this.cards) return;
      return this.cards.map((card) => ({
        name: card.name,
        id: card.id,
      }));
    },
    fieldList() {
      const fieldData =
        this.configTemplate && this.configTemplate[this.category];
      return fieldData && fieldData.AvailableFieldList;
    },
    hasChanges() {
      if (!this.updatedConfigValues.bulkEdit) return false;
      return !!Object.keys(this.updatedConfigValues.bulkEdit).length;
    },
    visibleCards() {
      return this.getVisibleCards(this.cards);
    },
    ...mapGetters({
      mergedConfig: "getMergedConfig",
      configTemplate: "getConfigTemplate",
      pendingCalls: "getPendingCallList",
      bulkUpdateCount: "getBulkUpdateCount",
      updatedConfigValues: "getUpdatedConfigValues",
      configVisibility: "getConfigVisibility",
    }),
  },
  asyncComputed: {
    async cards() {
      const configData = await this.configData();
      return configData.cards;
    },
    async category() {
      const configData = await this.configData();
      return configData.category;
    },
  },
  methods: {
    computedCategory(currentCard) {
      if (currentCard.category) {
        return currentCard.category;
      } else {
        return this.category;
      }
    },
    goToNextStep() {
      this.$router.push("/clients/bulkEdit/summary");
    },
    exitBulkSettings() {
      this.$store.dispatch("resetBulkSettings");
      this.$router.push("/clients");
    },
    getVisibleCards(cardsToFilter) {
      return visibleCards({
        cardsToFilter,
        category: this.category,
        configVisibility: this.configVisibility,
        id: "bulkEdit",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.config-tab {
  display: flex;
}
.instructions {
  h6 {
    margin-bottom: $qvo-spacing-16;
  }
}
.content {
  width: 100%;
  margin: $qvo-spacing-24;
}
.card {
  margin-bottom: $qvo-spacing-24;
}
.card-title {
  margin-top: $qvo-spacing-48;
}
h6 {
  margin-bottom: $qvo-spacing-24;
}
nav {
  padding-top: 0;
  position: sticky;
  top: $qvo-spacing-24;
}
.bottom-bar {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1;
}
</style>